import type { DailyLog, FoodSearchResult, Profile, User, WeightLog } from "@/types";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "include", // send the HttpOnly session cookie
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new ApiError(res.status, body.error ?? `Request to ${path} failed`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  auth: {
    loginWithGoogle: (idToken: string) =>
      request<{ user: User; hasProfile: boolean }>("/auth/google", {
        method: "POST",
        body: JSON.stringify({ idToken }),
      }),
    me: () => request<{ user: User; hasProfile: boolean }>("/auth/me"),
    logout: () => request<void>("/auth/logout", { method: "POST" }),
  },
  profile: {
    get: () => request<Profile>("/profile"),
    create: (profile: Omit<Profile, "userId">) =>
      request<Profile>("/profile", { method: "POST", body: JSON.stringify(profile) }),
    update: (partial: Partial<Omit<Profile, "userId">>) =>
      request<Profile>("/profile", { method: "PATCH", body: JSON.stringify(partial) }),
  },
  logs: {
    daily: {
      get: (date: string) => request<DailyLog | null>(`/logs/daily?date=${date}`),
      range: (start: string, end: string) =>
        request<DailyLog[]>(`/logs/daily?start=${start}&end=${end}`),
      upsert: (date: string, patch: Partial<Pick<DailyLog, "foodEntries" | "waterMl">>) =>
        request<DailyLog>("/logs/daily", {
          method: "PUT",
          body: JSON.stringify({ date, ...patch }),
        }),
    },
    weight: {
      range: (start: string, end: string) =>
        request<WeightLog[]>(`/logs/weight?start=${start}&end=${end}`),
      upsert: (date: string, weightKg: number) =>
        request<WeightLog>("/logs/weight", {
          method: "PUT",
          body: JSON.stringify({ date, weightKg }),
        }),
    },
  },
  food: {
    search: (query: string) =>
      request<FoodSearchResult[]>(`/food/search?q=${encodeURIComponent(query)}`),
  },
  account: {
    exportCsv: () => `/api/account/export`, // opened directly as a download link
    deleteAccount: () => request<void>("/account", { method: "DELETE" }),
  },
};

export { ApiError };
